import React from 'react';
import './Result.scss';
import classNames from 'classnames';
import { getPrefixedDataAttributes } from '@/components/utils/DomUtils';
import type { DataAttributes } from '@/components/utils/TypeUtils';
import Icon from '@/components/Icon/Icon';
import Button from '@/components/Button/Button';

interface LinkProps {
  /** Pass in the icon child to display */
  icon?: string;
  /** Primary content */
  primaryText?: string;
  /** Secondary content */
  secondarytText?: string;
  /** secondarytText an optional className to be added to your link component. */
  className?: string;
  /** The data-* attribute is used to store custom data private to the page or application. */
  dataAttributes?: DataAttributes;
  /** The status of the result */
  status?: string;
  /** Show try again button */
  showTryAgainButton?: boolean;
  /** Callback when clicked */
  onTryAgainClicked?: () => void;
}

/**
 * Primary UI component for user interaction
 */
const Result: React.FC<LinkProps> = ({ ...props }: LinkProps) => {
  const {
    icon = '/src/assets/svg/simpson_pointing_upward.svg',
    className,
    dataAttributes,
    status,
    primaryText = 'Uh-Oh! Something went wrong.',
    secondarytText = 'Can you try refreshing?',
    showTryAgainButton = false,
    onTryAgainClicked,
  } = props;
  const resultClass = classNames('result-wrapper', className);

  return (
    <div className={resultClass} {...getPrefixedDataAttributes(dataAttributes, 'Result')}>
      <div className="result">
        {status && <div className="result__status">{status}</div>}
        {icon && <Icon source={icon} className="result__icon" />}

        <div className="result__content">
          {primaryText && <div className="result__content__primary-text">{primaryText}</div>}
          {secondarytText && (
            <div className="result__content__secondary-text">{secondarytText}</div>
          )}
        </div>
      </div>
      {showTryAgainButton && (
        <div className="try-again-btn-container">
          <Button
            className="try-again-btn"
            iconClassName="try-again__icon"
            labelClassName="try-again__label"
            iconOnRightSrc="/src/assets/svg/refresh.svg"
            onClick={onTryAgainClicked}
          >
            Try again
          </Button>
        </div>
      )}
    </div>
  );
};

Result.displayName = 'Result';

export default Result;
